import { batch } from 'react-redux';

import { IGetCitiesTariffsQueryParams } from '~/src/api/content/cities/tariffs';
import FullBuyAnalytics from '~/src/features/full-buy/analytics';
import getFlow from '~/src/features/full-buy/helpers/getFlow';
import getEquipment from '~/src/features/full-buy/store/actions/getEquipment';
import { getBundleOfSubscriptions } from '~/src/features/full-buy/store/actions/getSubscriptions';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { TAppThunkDispatch, TGetState } from '~/src/store';

import { fetchTariffPackages } from '../functions';
import { ETariffFilters } from '../initialState';

import { setTariffFilters } from './filters';
import {
    selectTariff,
    setDisplayTariffLineState,
    setFlow,
    setInitialized,
    setTariffLineAlias,
    setTariffs,
} from './index';

export type TIncomingQueryString = {
    salePackageId?: string | undefined,
    tariffNameAlias?: string | undefined,
    monoTariff: boolean,
    landingAvailable?: string,
}

interface IInitParams {
    filters: ETariffFilters[] | undefined,
    tariffLineAlias: string | undefined,
    analytics: FullBuyAnalytics,
}

/**
 * Получение данных для страницы fullBuy
 */
export const initFullBuy = (
    {
        filters = [],
        tariffLineAlias,
        landingAvailable,

        salePackageId,
        tariffNameAlias,
        monoTariff,
        analytics,
    }: IInitParams & TIncomingQueryString,
) => async(dispatch: TAppThunkDispatch, getState: TGetState): Promise<void> => {
    const { providerId } = getState().city.provider;
    // We should remove the "Cottage" filter from the list of filters,
    // as other filters (except "Cottage") are involved in the main flow of the "Full Buy" logic.
    const withoutCottageFilters = filters?.filter(f => f !== ETariffFilters.COTTAGE) || [];
    const isCottage = filters?.some(f => f === ETariffFilters.COTTAGE) || false;

    const isAddressVerificationOnFirstStep = !tariffLineAlias &&
        withoutCottageFilters.length === 0 &&
        (Boolean(salePackageId) || Boolean(tariffNameAlias));

    const flow = getFlow(isAddressVerificationOnFirstStep, isCottage);

    const fetchTariffPackagesQueryParams: IGetCitiesTariffsQueryParams = {
        availableNew: 1,
    };

    if (landingAvailable) {
        fetchTariffPackagesQueryParams.landingAvailable = landingAvailable;
    }

    // Очень надеюсь, что это решение в скором времени будет изменено.
    // В админке контента признаки у тарифов 100Мбит/с. отключены, но имеются адреса, у которых только такая максимальная скорость может быть.
    // в api-content был добавлен флаг, позволющий получить отключенные тариф (не все, включаются некоторые в ручном режиме).
    if (filters?.includes(ETariffFilters.SPEED100)) {
        fetchTariffPackagesQueryParams.restrictIgnore = 1;
    }

    const tariffPackages = await fetchTariffPackages(
        providerId,
        fetchTariffPackagesQueryParams,
        isCottage,
    );

    if (tariffPackages) {
        batch(() => {
            dispatch(setTariffs(tariffPackages));
            dispatch(setTariffFilters(filters));
            dispatch(setTariffLineAlias(tariffLineAlias || ''));
            dispatch(getBundleOfSubscriptions(tariffPackages));
        });
    }

    if (tariffLineAlias || (!salePackageId && !tariffNameAlias)) {
        dispatch(setDisplayTariffLineState(true));
    }

    if (salePackageId) {
        dispatch(selectTariff({
            salePackageId: Number(salePackageId),
        }));
    }

    if (tariffNameAlias) {
        dispatch(selectTariff({
            tariffNameAlias,
            monoTariff,
        }));
    }

    // in case when user came from a direct link for tariff, we should send notification to the dataLayer
    if (salePackageId || tariffNameAlias || tariffLineAlias || (salePackageId && tariffLineAlias) || (tariffNameAlias && tariffLineAlias)) {
        const currentTariff = selectCurrentTariff(getState());

        if (analytics.category) {
            analytics.initializationByTariffLine(
                [
                    tariffLineAlias ?? tariffNameAlias,
                    currentTariff?.tariff_name,
                ]
                    .filter(Boolean).join(' | '),
            );
        }

        dispatch(getEquipment());
    }

    batch(() => {
        // set full-buy flow
        dispatch(setFlow(flow));
        dispatch(setInitialized(true, isAddressVerificationOnFirstStep));
    });
};
